import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { proofMessages } from "@/data/socialProofData";

const BRAND_GOLD = "#D4A843";
const SHOW_DURATION = 6000;    // How long the notification stays visible (6 seconds)
const CYCLE_INTERVAL = 30000;  // Show a new notification every 30 seconds

// Generate random time strings
const timesEn = [
  "just now", "1 min ago", "2 min ago", "3 min ago", "5 min ago",
  "7 min ago", "10 min ago", "12 min ago", "15 min ago", "18 min ago",
  "20 min ago", "25 min ago", "30 min ago", "35 min ago", "40 min ago",
  "45 min ago", "1 hour ago", "2 hours ago", "3 hours ago",
];
const timesAr = [
  "الآن", "قبل دقيقة", "قبل دقيقتين", "قبل 3 دقائق", "قبل 5 دقائق",
  "قبل 7 دقائق", "قبل 10 دقائق", "قبل 12 دقيقة", "قبل 15 دقيقة", "قبل 18 دقيقة",
  "قبل 20 دقيقة", "قبل 25 دقيقة", "قبل 30 دقيقة", "قبل 35 دقيقة", "قبل 40 دقيقة",
  "قبل 45 دقيقة", "قبل ساعة", "قبل ساعتين", "قبل 3 ساعات",
];

// Fisher-Yates shuffle
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function SocialProofBar() {
  const { t, lang } = useLanguage();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(false);

  // Shuffle messages once on mount so each session sees a different order
  const shuffled = useMemo(() => shuffle(proofMessages), []);

  const showNotification = useCallback(() => {
    setVisible(true);
    // Hide after SHOW_DURATION
    setTimeout(() => {
      setVisible(false);
    }, SHOW_DURATION);
  }, []);

  useEffect(() => {
    // First appearance after 5 seconds
    const initialTimer = setTimeout(() => {
      showNotification();
    }, 5000);

    return () => clearTimeout(initialTimer);
  }, [showNotification]);

  useEffect(() => {
    // After the first show, cycle every 30 seconds with a new random message
    const cycleTimer = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % shuffled.length);
      showNotification();
    }, CYCLE_INTERVAL);

    return () => clearInterval(cycleTimer);
  }, [shuffled.length, showNotification]);

  const msg = shuffled[currentIdx];
  // Pick a random time for each appearance
  const timeIdx = currentIdx % timesEn.length;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed bottom-24 left-4 md:left-6 z-40 bg-white shadow-xl border border-gray-100 px-4 py-3 max-w-xs cursor-pointer"
          onClick={() => setVisible(false)}
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold text-gray-900" style={{ backgroundColor: BRAND_GOLD }}>
              {lang === "en" ? msg.nameEn[0] : msg.nameAr[0]}
            </div>
            <div className="min-w-0">
              <motion.div
                key={currentIdx}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-xs text-gray-900 font-medium leading-relaxed">
                  <span className="font-bold">{lang === "en" ? msg.nameEn : msg.nameAr}</span>
                  {" "}{t("from", "من")}{" "}
                  <span>{lang === "en" ? msg.cityEn : msg.cityAr}</span>
                  {" "}{t("booked", "حجز")}{" "}
                  <span style={{ color: BRAND_GOLD }} className="font-semibold">{lang === "en" ? msg.serviceEn : msg.serviceAr}</span>
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">{lang === "en" ? timesEn[timeIdx] : timesAr[timeIdx]}</p>
              </motion.div>
            </div>
          </div>
          {/* Gold accent bar */}
          <div className="absolute top-0 left-0 h-full w-0.5" style={{ backgroundColor: BRAND_GOLD }} />
          {/* Close hint */}
          <button
            onClick={(e) => { e.stopPropagation(); setVisible(false); }}
            className="absolute top-1 right-1.5 text-gray-300 hover:text-gray-500 text-xs leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
